import React, { useEffect, useState } from 'react'
import CalendarContainer from './container'
import { useGetVendorTablesQuery } from 'pages/tables/services';
import { useNavigate } from 'react-router-dom';
import { OrderProvider } from 'context/OrderContext';
import OrderDialogManager from 'components/Modal/OrderDialog/OrderDialogManager';
import { formatCurrency, parseUtcToLocalDate, convertTo24Hour, getOrderTypePermissions, isPermissionAllowed } from 'utils/function';
import { useSelector } from 'react-redux';
import { useOrder } from 'context/OrderContext';
import { useGetCalendarDataQuery, useGetTableTimeSlotsQuery } from './services';
import ViewOrderDialog from 'components/Modal/ViewOrderDialog';

const Calendar = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const formatDate = currentDate.toLocaleDateString('en-CA');
  const userData = useSelector((state) => state?.posData);
  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);

  const [refetchMain, setRefetchMain] = useState(false);
  const { openDialog } = useOrder();
  const { data: tablesListing, isLoading: isTablesLoading } = useGetVendorTablesQuery({ page: 'all' });

  const isWeekend = [0, 6].includes(currentDate.getDay());
  const { data: timeSlotsData } = useGetTableTimeSlotsQuery({ is_weekend: isWeekend });
  const { data: calendarData, isLoading: isCalendarLoading, isFetching: isCalendarFetching, refetch } = useGetCalendarDataQuery({ date: formatDate }, { refetchOnMountOrArgChange: true });

  const isDataLoading = isCalendarLoading || isTablesLoading || (isCalendarFetching && !calendarData?.data);


  useEffect(() => {
    if (refetchMain) {
      refetch();
    }
    return (setRefetchMain(false))
  }, [refetchMain])

  const handleDatesSet = (arg) => {
    setCurrentDate(arg.start);
  };


  const permissions = React.useMemo(() => getOrderTypePermissions(userData), [userData]);
  const isTableBookingAllowed = isPermissionAllowed(permissions, 'permission_table_booking');

  // when an event is clicked
  const handleEventClick = (event) => {
    if (event.extendedProps?.isAvailable) {
      if (!isTableBookingAllowed) return;
      // Pre-fill Table Booking
      const tableId = event.extendedProps.tableId;
      const startTime = event.extendedProps.timeStr; // e.g. "09:00AM"
      const slotName = event.extendedProps.slotName; // e.g. "Breakfast"

      const day = currentDate.getDate();
      const month = currentDate.toLocaleString("default", { month: "short" }).toUpperCase();
      const isToday = currentDate.toDateString() === new Date().toDateString();
      const bookingDateValue = isToday ? "Today" : `${day} ${month}`;

      openDialog('tableBooking', {
        table: { id: parseInt(tableId), table_no: event.extendedProps.tableNo },
        day: bookingDateValue,
        time: startTime,
        time_slot: slotName
      });
      return;
    }
    // `event.id` is the booking/order id
    setViewOrder({ id: event.id, total_item_count: event.extendedProps?.guestCount });
    setOpenModal(true);
  };

  const handleCloseViewDialog = () => {
    setOpenModal(false);
    // setViewOrder(null);
  };


  const resources = tablesListing?.data?.records.map((rec) => ({
    id: rec.id.toString(),
    title: rec.table_no,
    capacity: rec.capacity,
    notes: rec.notes,
  })) || [];

  const orderSummary = [
    {
      id: "1",
      title: "Total Orders",
      subtitle: "(Today)",
      value: `${calendarData?.data?.stats?.totalOrdersToday || 0}`,
      highlight: true,
    },
    {
      id: "2",
      title: "Available Tables",
      value: `${calendarData?.data?.stats?.availableTablesNow || 0}/${calendarData?.data?.stats?.totalTables || 0}`,
      highlight: true,
    },
    {
      id: "3",
      title: "Today’s Revenue",
      value: formatCurrency(
        calendarData?.data?.stats?.todayRevenue || 0,
        userData?.vendor?.currency
      ),
      highlight: true,
    },
  ]

  const bookings =
    calendarData?.data?.bookings?.map((b) => {
     // parse date + time into a JS Date
    //  const start = new Date(`${b.booking_date}T${b.time}z`);
    //  // for demo we'll make every booking last 1 hour:
    //  const end = new Date(start.getTime() + 60 * 60 * 1000);
    //  const now = new Date()
      const start = parseUtcToLocalDate(b.booking_date, b.time);
      // const end = new Date(start.getTime() + 60 * 60 * 1000);
      // Use dynamic end_time if available, otherwise default to 1hr
      let end;
      if (b.end_time) {
        end = parseUtcToLocalDate(b.booking_date, b.end_time);
      } else {
        end = new Date(start.getTime() + 60 * 60 * 1000);
      }

      const now = new Date();

      // choose a color by status or type
     let backgroundColor = '#6CFF66'; // default grey
     let borderColor = '#1F9619'; // default grey
      let opacity = 1;

      if (start > now) {
        backgroundColor = '#6CFF66'                   // future bookings
        borderColor = "#1F9619"
      } else if (now - start <= 40 * 60 * 1000) {
        backgroundColor = '#6CFF66'
        borderColor = "#1F9619"                 // first 40 mins
      } else if (now - start <= 60 * 60 * 1000) {
        backgroundColor = '#FCD29B'                   // 40–60 mins
        borderColor = "#E86400"
      } else {
        backgroundColor = '#FCB0AF'                   // past 60 mins
        borderColor = "#D41616"
      }

    if(b.status == "completed" || b.status == "cancel" || b.status == "reject"){
        backgroundColor = '#E2E2E2'
        borderColor = "#4D4D4D"
        opacity = 0.8
      }

     // if entire booking is in the past (booking_date < today):
    // if (end < new Date(now.toDateString())) {
    //   backgroundColor = '#E2E2E2'
    //   borderColor = "#4D4D4D"
    //   opacity = 0.8
    // }

      return {
        id: String(b.id),
        orderId: String(b.order_id),
        resourceId: b?.table_id?.toString(),
        title: `${b.customerInfo?.customer_full_name}`,
        extendedProps: {
          guestCount: b.guestCount,
          borderColor: borderColor,
          orderId: String(b.order_id),
          opacity: opacity
        },
        start,
        end,
        backgroundColor,
      };
    }) || [];

  // Generate "Available" slots
  const availableSlots = [];
  if (timeSlotsData && tablesListing?.data?.records) {
    const now = new Date();
    const isToday = currentDate.toDateString() === now.toDateString();

    tablesListing.data.records.forEach((table) => {
      // 1. Get all bookings for this table
      const tableBookings = bookings
        .filter(b => b.resourceId === table.id.toString())
        .sort((a, b) => a.start - b.start);

      // 2. Collect all "available" slot starts (those not starting exactly at a booking time)
      const tableAvailableStarts = [];
      timeSlotsData.forEach((category) => {
        category.slots.forEach((slotObj) => {
          const slot = slotObj.time;
        // category.slots.forEach((slot) => {
          // Parse slot time e.g. "09:00AM"
          const [time, period] = [slot.slice(0, 5), slot.slice(5)];
          let [hours, minutes] = time.split(':').map(Number);
          if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12;
          if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;

          const slotStart = new Date(currentDate);
          slotStart.setHours(hours, minutes, 0, 0);
          // const slotEnd = new Date(slotStart.getTime() + 60 * 60 * 1000); // 1 hour duration

          // Only future slots for today
          if (isToday && slotStart <= now) return;

          // Check if this slot start falls INSIDE an existing booking's duration
          const isSlotBlocked = tableBookings.some(b => {
            return slotStart >= b.start && slotStart < b.end;
          });
          if (isSlotBlocked) return;

          tableAvailableStarts.push({
            start: slotStart,
            timeStr: slot,
            slotName: category.name
          });
        });
      });

      // 3. Create a merged timeline of all "event starts" (bookings and available slots)
      const mergedTimeline = [
        ...tableBookings.map(b => ({ start: b.start })),
        ...tableAvailableStarts.map(s => ({ start: s.start }))
      ].sort((a, b) => a.start - b.start);

      // 4. Generate the availableSlots objects with clipped durations
      tableAvailableStarts.forEach((s) => {
        // Find the next start time in the merged timeline
        const nextStart = mergedTimeline.find(t => t.start > s.start)?.start;

        const defaultEnd = new Date(s.start.getTime() + 60 * 60 * 1000); // 1 hour for large gaps
        const fillThresholdEnd = new Date(s.start.getTime() + 90 * 60 * 1000); // Fill gaps up to 90 mins
        let slotEnd = defaultEnd;

        if (nextStart) {
          // If next start is within 90 minutes, clip exactly to it to fill the gap
          if (nextStart <= fillThresholdEnd) {
            slotEnd = nextStart;
          } else {
            // If next start is far away (> 90 mins), stay with 1 hour
            slotEnd = defaultEnd;
          }
        }

        availableSlots.push({
          id: `avail-${table.id}-${s.timeStr}`,
          resourceId: table.id.toString(),
          title: 'Avl',
          start: s.start,
          end: slotEnd,
          backgroundColor: '#FFFFFF',
          extendedProps: {
            isAvailable: true,
            timeStr: s.timeStr,
            slotName: s.slotName,
            tableNo: table.table_no,
            tableId: table.id,
            borderColor: '#E2E2E2'
          }
        });
      });
    });
  }

  const allEvents = [...bookings, ...availableSlots];

  const handleViewPastOrders = () => {
    navigate("/orders/past-orders");
  }

  return (
    <>
      <CalendarContainer
        tables={resources}
        bookings={allEvents}
        orderSummary={orderSummary}
        handleViewPastOrders={handleViewPastOrders}
        currentDate={currentDate}
        setCurrentDate={setCurrentDate}
        handleDatesSet={handleDatesSet}
        onEventClick={handleEventClick}
        isLoading={isDataLoading}
      />
      <OrderDialogManager />

      {viewOrder && (
        <ViewOrderDialog
          data={viewOrder}
          open={openModal}
          onClose={handleCloseViewDialog}
          refetchMain={refetchMain}
          setRefetchMain={setRefetchMain}
          setOpenModal={setOpenModal}
        />
      )}
    </>
  )
}

export default Calendar
